import React, { useEffect } from "react";
import { Table, Tag, Button, Space, Popconfirm, message } from "antd";
import { EditOutlined, RollbackOutlined, StopOutlined } from "@ant-design/icons";
import { useAtom, useSetAtom } from "jotai";
import {
    anioAtom,
    programacionVacacionesAtom,
    paginaActualAtom,
    tamanioPaginadoAtom,
    totalRegistrosAtom,
    filtroEstadoAtom,
    loadingTablaAtom,
    recargarTablaAtom,
    selectedRowKeysAtom,
} from "../../ProgramarVacacionesStore";
import { ProgramacionService } from "services/VacacionesService";

const COLORES_ESTADO = {
    PENDIENTE: "gold",
    APROBADO: "green",
    RECHAZADO: "red",
    CANCELADO: "red",
};

const LABEL_ESTADO = {
    CANCELADO: "RECHAZADO",
};

const TablaProgramarVacaciones = ({ onEditar }) => {
    const [programacionVacaciones, setProgramacionVacaciones] = useAtom(programacionVacacionesAtom);
    const [totalRegistros, setTotalRegistros] = useAtom(totalRegistrosAtom);
    const [paginaActual, setPaginaActual] = useAtom(paginaActualAtom);
    const [tamanioPaginado] = useAtom(tamanioPaginadoAtom);
    const [filtroEstado] = useAtom(filtroEstadoAtom);
    const [anio] = useAtom(anioAtom);
    const [loading, setLoading] = useAtom(loadingTablaAtom);
    const [recargar] = useAtom(recargarTablaAtom);
    const [selectedRowKeys, setSelectedRowKeys] = useAtom(selectedRowKeysAtom);
    const setRecargar = useSetAtom(recargarTablaAtom);

    useEffect(() => {
        cargarDatos(paginaActual);
    }, [anio, filtroEstado, recargar]);

    const cargarDatos = async (pagina) => {
        setLoading(true);
        const params = { anio, pagina, tamano: tamanioPaginado };
        if (filtroEstado && filtroEstado !== "TODOS") {
            params.estado = filtroEstado;
        }
        const response = await ProgramacionService.listar(params);
        setLoading(false);
        if (response.estado === "EXITO") {
            setProgramacionVacaciones(response.data.registros || []);
            setTotalRegistros(response.data.total || 0);
        }
    };

    const onChangePaginacion = (pagina) => {
        setPaginaActual(pagina);
        cargarDatos(pagina);
    };

    const onCancelar = async (id) => {
        const response = await ProgramacionService.cancelar(id);
        if (response.estado === "EXITO") {
            message.success("Programación rechazada.");
            setRecargar((v) => v + 1);
        } else {
            message.error(response.mensaje || "No se pudo rechazar.");
        }
    };

    const onAnular = async (id) => {
        const response = await ProgramacionService.anular(id);
        if (response.estado === "EXITO") {
            message.success("Programación anulada correctamente.");
            setRecargar((v) => v + 1);
        } else {
            message.error(response.mensaje || "No se pudo anular.");
        }
    };

    const columnas = [
        {
            title: "DNI",
            dataIndex: "colaboradorDni",
            align: "center",
            width: 110,
        },
        {
            title: "Colaborador",
            dataIndex: "colaboradorNombre",
            align: "left",
        },
        {
            title: "Tipo",
            dataIndex: "tipoNombre",
            align: "center",
        },
        {
            title: "Fecha Inicio",
            dataIndex: "fechaInicio",
            align: "center",
            width: 120,
        },
        {
            title: "Fecha Fin",
            dataIndex: "fechaFin",
            align: "center",
            width: 120,
        },
        {
            title: "Días",
            dataIndex: "diasSolicitados",
            align: "center",
            width: 70,
        },
        {
            title: "Estado",
            dataIndex: "estado",
            align: "center",
            width: 110,
            render: (estado) => (
                <Tag color={COLORES_ESTADO[estado] || "default"}>{LABEL_ESTADO[estado] || estado}</Tag>
            ),
        },
        {
            title: "Acciones",
            align: "center",
            width: 110,
            render: (_, record) => {
                if (record.estado === "PENDIENTE") {
                    return (
                        <Space size="small">
                            <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => onEditar && onEditar(record)}
                            />
                            <Popconfirm
                                title="¿Rechazar esta programación?"
                                okText="Sí"
                                cancelText="No"
                                onConfirm={() => onCancelar(record.id)}
                            >
                                <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<StopOutlined />}
                                />
                            </Popconfirm>
                        </Space>
                    );
                }
                if (record.estado === "APROBADO") {
                    return (
                        <Popconfirm
                            title="¿Anular esta aprobación? Se eliminará la solicitud y se restituirán los días."
                            okText="Sí, anular"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => onAnular(record.id)}
                        >
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<RollbackOutlined />}
                            >
                                Anular
                            </Button>
                        </Popconfirm>
                    );
                }
                return null;
            },
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (record) => ({
            disabled: record.estado !== "PENDIENTE",
        }),
    };

    return (
        <Table
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            columns={columnas}
            dataSource={programacionVacaciones}
            loading={loading}
            pagination={{
                pageSize: tamanioPaginado,
                total: totalRegistros,
                current: paginaActual,
                showTotal: (total) => `Total ${total} registros`,
                onChange: onChangePaginacion,
            }}
        />
    );
};

export default TablaProgramarVacaciones;
